import { supabase, run } from './db';
import { meuCadastro } from './cliente';
import { addDays, SCHEDULE_BY_ID } from '../constants/schedule';

/**
 * Agenda do técnico: o que ele vai fazer, pelo link.
 *
 * Mesma porta do cliente (link + senha, a mesma tabela de links e de sessões),
 * outro conteúdo. Tudo passa por função do banco, e todas derivam o técnico da
 * sessão: nenhuma aceita `technician_id`, então não existe o bug de pedir a
 * agenda de outro.
 */

export const meuCadastroDeTecnico = async (token) => {
  const linhas = await run(supabase.rpc('cabecalho_do_tecnico_por_token', { p_token: token }));
  return linhas?.[0] || null;
};

/**
 * De quem é este link: cliente ou técnico.
 *
 * O cliente é perguntado primeiro porque é o caso de quase todo link. Um link
 * de técnico não abre nada da área do cliente (o banco resolve `client_id`,
 * que ali é nulo), então a primeira pergunta volta vazia e cai na segunda.
 */
export const quemEntrouPeloLink = async (token) => {
  const cliente = await meuCadastro(token);
  if (cliente) return { tipo: 'cliente', cadastro: cliente };
  const tecnico = await meuCadastroDeTecnico(token);
  return tecnico ? { tipo: 'tecnico', cadastro: tecnico } : null;
};

export const minhaAgenda = (token) =>
  run(supabase.rpc('agenda_do_tecnico_por_token', { p_token: token }));

/** Ficha do cliente e o que entra na etapa. Nulo se a etapa não é dele. */
export const minhaEtapa = (token, serviceId, etapa) =>
  run(supabase.rpc('etapa_do_tecnico_por_token', {
    p_token: token, p_service_id: serviceId, p_etapa: etapa,
  }));

/**
 * A coordenada de um link curto de mapa, para o botão do Waze. Quem segue o
 * redirecionamento é o servidor (`coordenadas-do-mapa`); falhando, volta nulo
 * e o botão do Waze simplesmente não aparece — o do mapa continua.
 */
export const coordenadasDoMapa = async (url) => {
  try {
    const { data, error } = await supabase.functions.invoke('coordenadas-do-mapa', { body: { url } });
    if (error || data?.lat == null) return null;
    return { lat: data.lat, lng: data.lng };
  } catch {
    return null;
  }
};

/* ------------------------------------------------------------------ */
/* Do lado do laboratório: gerar, trocar senha, revogar                */
/* ------------------------------------------------------------------ */

export const gerarLinkDoTecnico = (technicianId, senha, dias = 180) =>
  run(supabase.rpc('gerar_link_do_tecnico', { p_technician_id: technicianId, p_senha: senha, p_dias: dias }));

export const linkDoTecnico = (technicianId) =>
  run(supabase
    .from('client_access_links')
    .select('*')
    .eq('technician_id', technicianId)
    .is('revogado_em', null)
    .maybeSingle());

export const redefinirSenhaDoTecnico = (technicianId, senha) =>
  run(supabase.rpc('redefinir_senha_do_link_do_tecnico', { p_technician_id: technicianId, p_senha: senha }));

export const mensagemDoLinkDoTecnico = ({ tecnico, url, senha }) => {
  const abertura = tecnico ? `Olá, ${tecnico}!` : 'Olá!';
  const linhas = [`${abertura} Sua agenda de trabalhos está aqui:`, url];
  if (senha) linhas.push('', `Senha de acesso: ${senha}`);
  return linhas.join('\n');
};

/* ------------------------------------------------------------------ */
/* A agenda em dias                                                    */
/* ------------------------------------------------------------------ */

export const ROTULO_DA_ETAPA = {
  ...Object.fromEntries(Object.values(SCHEDULE_BY_ID).map(e => [e.id, e.label])),
  retirada: 'Retirada',
};

/**
 * As linhas do banco viram EVENTOS, e os eventos viram dias.
 *
 * O protocolo rende dois eventos — a implantação e a retirada (D+7) —, porque
 * são duas idas à fazenda. A implantação só aparece enquanto não foi lançada;
 * a retirada, enquanto a data dela não passou. Os dois abrem a mesma etapa
 * (`etapa: 'protocolo'`), que é onde mora a equipe e a observação.
 *
 * Volta `{ atrasadas, dias, semData }`:
 *   atrasadas — etapa não lançada com data antes de hoje, em cima: é o que
 *               mais precisa de atenção, e enterrada no passado ninguém vê.
 *   dias      — [{ data, eventos }] de hoje em diante, em ordem.
 *   semData   — etapa escalada que ainda não tem dia marcado.
 */
export const agendaEmDias = (linhas = [], hoje) => {
  const eventos = [];
  (linhas || []).forEach(l => {
    const base = { ...l, tipo: l.etapa };
    if (l.etapa === 'protocolo') {
      if (!l.feito) eventos.push(base);
      if (l.data) {
        const retirada = addDays(l.data, 7);
        if (retirada >= hoje) eventos.push({ ...base, tipo: 'retirada', data: retirada });
      }
      return;
    }
    eventos.push(base);
  });

  const atrasadas = [];
  const semData = [];
  const porDia = new Map();
  eventos.forEach(e => {
    if (!e.data) { semData.push(e); return; }
    const dia = String(e.data).slice(0, 10);
    if (dia < hoje) { atrasadas.push(e); return; }
    if (!porDia.has(dia)) porDia.set(dia, []);
    porDia.get(dia).push(e);
  });

  const dias = [...porDia.keys()].sort().map(data => ({ data, eventos: porDia.get(data) }));
  atrasadas.sort((a, b) => String(a.data).localeCompare(String(b.data)));
  return { atrasadas, dias, semData };
};

/** "Hoje", "Amanhã" ou "sáb., 31 de out." — como uma agenda de celular. */
export const rotuloDoDia = (iso, hoje) => {
  if (iso === hoje) return 'Hoje';
  if (iso === addDays(hoje, 1)) return 'Amanhã';
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString('pt-BR', {
    weekday: 'short', day: 'numeric', month: 'short', timeZone: 'UTC',
  });
};
